import React from 'react';

/**
 * Renders a long string with middle-ellipsis truncation.
 *
 * Pure CSS — no JS measurement. Splits the input into:
 *   head: leading characters, truncates with `text-overflow: ellipsis`
 *   tail: the last `tailChars` characters, never truncated
 *
 * Example: "this_is_an_extremely_long_filename_2026Q1.csv"
 *          renders as: "this_is_an_extremely_long_filenam…2026Q1.csv"
 *
 * Props:
 *   value      — string to display
 *   tailChars  — characters to keep visible on the right (default 12)
 *   className  — applied to the outer flex container (e.g. max-w-[420px])
 *   title      — hover tooltip override (defaults to full value)
 *
 * Usage: parent must constrain width via className (e.g. `max-w-[420px]`).
 */
const MiddleTruncate = ({ value = '', tailChars = 12, className = '', title, ...rest }) => {
  const s = String(value);
  if (s.length <= tailChars + 4) {
    // Short enough that middle-truncation would be pointless
    return (
      <span className={`inline-block whitespace-nowrap ${className}`} title={title ?? s} {...rest}>
        {s}
      </span>
    );
  }
  const head = s.slice(0, s.length - tailChars);
  const tail = s.slice(-tailChars);
  return (
    <span
      className={`inline-flex items-baseline min-w-0 whitespace-nowrap ${className}`}
      title={title ?? s}
      {...rest}
    >
      <span className="truncate min-w-0 flex-1">{head}</span>
      <span className="shrink-0">{tail}</span>
    </span>
  );
};

export default MiddleTruncate;
